import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { apiService as API, DriverPerformance } from "../services/api";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Search } from 'lucide-react';
import { 
  Box, 
  Truck, 
  Star1,
  Profile2User,
  TruckFast,
  TruckTime,
  BoxTick
} from 'iconsax-react';
import SingleDateRangePicker from '../components/SingleDateRangePicker';
import CustomDropdown from '../components/CustomDropdown';
import { useAuth } from '../contexts/AuthContext';

/* ---------- Shimmer Skeleton Component ---------- */
const StatCardSkeleton = ({ iconBgColor }: { iconBgColor?: string }) => {
  return (
    <div className="group bg-white px-4 py-6 rounded-lg border border-gray-200 overflow-hidden relative">
      <div className="flex items-center justify-between relative">
        <div className="flex items-center space-x-4">
          <div className={`rounded-lg p-3 ${iconBgColor || 'bg-gray-100'} opacity-30`}>
            <div className="w-6 h-6 bg-gray-300/50 rounded animate-pulse"></div>
          </div>
          <div className="flex-1">
            <div className="h-4 w-24 bg-gray-200/60 rounded mb-2 animate-pulse"></div>
            <div className="h-8 w-20 bg-gray-200/60 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  );
};


/* ---------- Chart Skeleton Component ---------- */
const ChartSkeleton = () => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="h-6 w-48 bg-gray-200/60 rounded animate-pulse"></div>
      </div>
      <div className="p-6">
        <div className="h-64 bg-gray-100/60 rounded animate-pulse"></div>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const vendorDisplayName = (user?.name || '').trim() || 'Vendor';

  // Filter states
  const [dateRange, setDateRange] = useState('30'); // days
  const [vehicleType, setVehicleType] = useState('all');
  const [driverStatus, setDriverStatus] = useState('all');
  const [temperatureRange, setTemperatureRange] = useState('all');

  // Loaded data
  const [drivers, setDrivers] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [driverPerformance, setDriverPerformance] = useState<DriverPerformance[]>([]);
  /** Median feedback rating across all vendor drivers (from dashboard API when top drivers load). */
  const [vendorMedianRating, setVendorMedianRating] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingTopDrivers, setLoadingTopDrivers] = useState<boolean>(false);
  const [violationSearch, setViolationSearch] = useState('');
  const [driverSearch, setDriverSearch] = useState('');

  // Date range states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Cache for dashboard data - Optimizes API calls by caching responses for 5 minutes
  const cacheRef = useRef<{
    data: any;
    timestamp: number;
    key: string;
  } | null>(null);
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const normalizePhone = (num?: string) =>
    (num || '')
      .toString()
      .trim()
      .replace(/\s+/g, '')
      .replace(/^\+?91/, '')
      .replace(/^0+/, '');

  // Generate cache key based on filters
  const getCacheKey = useCallback((startDate: string, endDate: string) => {
    return `dashboard_${startDate}_${endDate}`;
  }, []);

  // Check if cached data is still valid
  const getCachedData = useCallback((key: string) => {
    if (cacheRef.current && cacheRef.current.key === key) {
      const age = Date.now() - cacheRef.current.timestamp;
      if (age < CACHE_DURATION) {
        return cacheRef.current.data;
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load topDrivers separately for better performance
  const loadingTopDriversRef = useRef(false);
  const loadTopDrivers = useCallback(async () => {
    if (loadingTopDriversRef.current) return;
    
    loadingTopDriversRef.current = true;
    setLoadingTopDrivers(true);
    setVendorMedianRating(null);
    try {
      const params: { startDate?: string; endDate?: string; includeTopDrivers?: string } = {};
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      params.includeTopDrivers = 'true';
      
      const dashboardResp = await API.getVendorDashboard(params);
      if (dashboardResp.success && dashboardResp.data) {
        const d = dashboardResp.data;
        const topDriversList = Array.isArray(d.topDrivers) ? d.topDrivers : [];
        const rawMedian = d.medianDriverRating;
        const median =
          typeof rawMedian === 'number' && !Number.isNaN(rawMedian)
            ? rawMedian
            : null;
        setDriverPerformance(topDriversList);
        setVendorMedianRating(median);

        const cacheKey = getCacheKey(startDate, endDate);
        if (cacheRef.current?.key === cacheKey) {
          cacheRef.current.data = {
            ...cacheRef.current.data,
            topDrivers: topDriversList,
            medianDriverRating: median,
          };
        }
      }
    } catch (e) {
      console.error("Error loading top drivers:", e);
    } finally {
      loadingTopDriversRef.current = false;
      setLoadingTopDrivers(false);
    }
  }, [startDate, endDate, getCacheKey]);

  // Load dashboard data with caching and query parameters
  const loadDashboardData = useCallback(async (useCache = true) => {
    const cacheKey = getCacheKey(startDate, endDate);
    
    // Check cache first - avoids unnecessary API calls
    if (useCache) {
      const cached = getCachedData(cacheKey);
      if (cached) {
        setDrivers(cached.drivers || []);
        setVehicles(cached.vehicles || []);
        setAssignments(cached.assignments || []);
        setDriverPerformance(cached.topDrivers || []);
        const m = cached.medianDriverRating;
        setVendorMedianRating(
          typeof m === 'number' && !Number.isNaN(m) ? m : null,
        );
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    try {
      // Only send date range if both dates are available
      const params: { startDate?: string; endDate?: string; limit?: number; includeTopDrivers?: string } = {};
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      // Limit assignments to reduce payload size (backend should handle this)
      params.limit = 500; // Reduced for faster response
      // Don't include topDrivers in initial load for faster response
      params.includeTopDrivers = 'false';

      const dashboardResp = await API.getVendorDashboard(params);

      if (dashboardResp.success && dashboardResp.data) {
        const dashboardData = dashboardResp.data;
        
        const driverList = Array.isArray(dashboardData.drivers) ? dashboardData.drivers : [];
        const vehicleList = Array.isArray(dashboardData.vehicles) ? dashboardData.vehicles : [];
        const assignmentList = Array.isArray(dashboardData.assignments) ? dashboardData.assignments : [];
        const topDriversList = Array.isArray(dashboardData.topDrivers) ? dashboardData.topDrivers : [];

        // Cache the data
        cacheRef.current = {
          data: {
            drivers: driverList,
            vehicles: vehicleList,
            assignments: assignmentList,
            topDrivers: topDriversList,
            medianDriverRating:
              typeof dashboardData.medianDriverRating === 'number' &&
              !Number.isNaN(dashboardData.medianDriverRating)
                ? dashboardData.medianDriverRating
                : null,
          },
          timestamp: Date.now(),
          key: cacheKey,
        };

        setDrivers(driverList);
        setVehicles(vehicleList);
        setAssignments(assignmentList);
        setDriverPerformance(topDriversList);
        
        // Load topDrivers separately after main data is loaded (lazy loading)
        // This improves initial load time significantly
        if (topDriversList.length === 0 && driverList.length > 0) {
          // Use setTimeout to load after main render completes
          setTimeout(() => {
            loadTopDrivers();
          }, 100);
        }
      } else {
        console.warn('Dashboard API returned unsuccessful response:', dashboardResp);
        setDrivers([]);
        setVehicles([]);
        setAssignments([]);
        setDriverPerformance([]);
        setVendorMedianRating(null);
      }
    } catch (e) {
      console.error("Error loading dashboard data:", e);
      setDrivers([]);
      setVehicles([]);
      setAssignments([]);
      setDriverPerformance([]);
      setVendorMedianRating(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, getCacheKey, getCachedData, loadTopDrivers]);

  // Initialize date range on mount
  useEffect(() => {
    if (dateRange !== 'custom') {
      const days = parseInt(dateRange, 10) || 30;
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - days);
      setStartDate(`${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`);
      setEndDate(`${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`);
    }
  }, [dateRange]);

  // Initial load - wait for dates to be initialized
  useEffect(() => {
    if (startDate && endDate) {
      loadDashboardData(true);
    }
  }, [startDate, endDate, loadDashboardData]); // Run when dates are initialized

  // Debounced reload when date range changes (but not on initial load)
  const isInitialLoadRef = useRef(true);
  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return; // Skip debounce on initial load
    }
    
    if (startDate && endDate) {
      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Set new timer for debounced reload
      debounceTimerRef.current = setTimeout(() => {
        loadDashboardData(false); // Don't use cache when filters change
      }, 500); // 500ms debounce
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [startDate, endDate, loadDashboardData, loadTopDrivers]);

  const inDateRange = useCallback((createdAt?: string) => {
    if (!createdAt) return false;
    const d = new Date(createdAt);
    if (dateRange === 'custom' && startDate && endDate) {
      const s = new Date(startDate);
      const e = new Date(endDate);
      if (d < s) return false;
      if (d > e) return false;
      return true;
    }
    const days = parseInt(dateRange, 10) || 30;
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - days + 1);
    return d >= start && d <= now;
  }, [dateRange, startDate, endDate]);

  const vendorDriverPhones = useMemo(() => new Set(
    drivers.map(d => normalizePhone((d as any).phoneNumber || (d as any).mobileNumber)).filter(Boolean)
  ), [drivers]);

  const phoneToDriver = useMemo(() => {
    const m = new Map<string, any>();
    drivers.forEach(d => {
      const ph = normalizePhone((d as any).phoneNumber || (d as any).mobileNumber);
      if (ph) m.set(ph, d);
    });
    return m;
  }, [drivers]);

  const vehicleNoToType = useMemo(() => {
    const m = new Map<string, string>();
    vehicles.forEach(v => {
      const no = (v as any).vehicleNumber;
      const type = (v as any).vehicleType || (v as any).type || '';
      if (no) m.set(no, String(type).toLowerCase());
    });
    return m;
  }, [vehicles]);

  const scopedAssignments = useMemo(() =>
    assignments.filter(a => {
      const phone = normalizePhone(a?.driverNumber);
      return vendorDriverPhones.size ? vendorDriverPhones.has(phone) : true;
    }), [assignments, vendorDriverPhones]
  );

  const filteredAssignments = useMemo(() => {
    return scopedAssignments.filter(a => {
      if (!inDateRange(a?.createdAt)) return false;
      if (vehicleType !== 'all') {
        const vt = vehicleNoToType.get(a?.vehicleNumber || '') || '';
        if (vt !== vehicleType) return false;
      }
      if (driverStatus !== 'all') {
        const drv = phoneToDriver.get(normalizePhone(a?.driverNumber));
        const st = (drv?.status || drv?.isActive === false ? 'Inactive' : 'Active');
        if (driverStatus === 'active' && st !== 'Active') return false;
        if (driverStatus === 'inactive' && st !== 'Inactive') return false;
      }
      if (temperatureRange !== 'all') {
        const tempReq = !!a?.tempRequired;
        const tmin = typeof a?.tempMin === 'number' ? a.tempMin : Number.NEGATIVE_INFINITY;
        if (temperatureRange === 'cold' && !(tempReq)) return false;
        if (temperatureRange === 'frozen' && !(tempReq && tmin <= -18)) return false;
        if (temperatureRange === 'ambient' && tempReq) return false;
      }
      return true;
    });
  }, [scopedAssignments, vehicleType, vehicleNoToType, driverStatus, phoneToDriver, temperatureRange, inDateRange]);

  // Delivery metrics calculations - Use unfiltered data (scopedAssignments) for metrics cards
  const ongoingDeliveries = useMemo(() => {
    return scopedAssignments.filter(a => {
      const status = a.deliveryStatus?.toLowerCase() || '';
      return status === 'ongoing' || status === 'loading_vehicle' || status === 'pickup_point' || status === 'close';
    }).length;
  }, [scopedAssignments]);

  const totalDeliveries = useMemo(() => scopedAssignments.length, [scopedAssignments]);

  const deliveredDeliveries = useMemo(() => {
    return scopedAssignments.filter(a => {
      const status = a.deliveryStatus?.toLowerCase() || '';
      return a.deliveryCompleted === true || 
             status === 'delivered' || 
             status === 'completedbyadmin' ||
             a.status === 'completed';
    }).length;
  }, [scopedAssignments]);

  const upcomingDeliveries = useMemo(() => {
    return scopedAssignments.filter(a => {
      const status = a.deliveryStatus?.toLowerCase() || '';
      return status === 'upcoming' || status === 'pending';
    }).length;
  }, [scopedAssignments]);

  const totalVendorCost = useMemo(() => {
    return scopedAssignments.reduce((sum, assignment) => {
      // Keep compatibility with different backend shapes used across vendor APIs.
      const rawCost =
        assignment?.costToVendor ??
        assignment?.costWithoutTax ??
        assignment?.totalCost ??
        assignment?.cost ??
        0;
      const cost = Number(rawCost);
      return sum + (Number.isFinite(cost) ? cost : 0);
    }, 0);
  }, [scopedAssignments]);

  const formatMetricCurrency = useCallback((amount: number) => {
    const value = Math.max(0, Number(amount) || 0);
    if (value >= 1_000_000) {
      const inMillions = value / 1_000_000;
      const compact = inMillions >= 10 ? inMillions.toFixed(0) : inMillions.toFixed(1);
      return `₹${compact}M`;
    }
    if (value >= 1_000) {
      const inThousands = value / 1_000;
      const compact = inThousands >= 10 ? inThousands.toFixed(0) : inThousands.toFixed(1);
      return `₹${compact}K`;
    }
    return `₹${Math.round(value).toLocaleString('en-IN')}`;
  }, []);

  const metrics = useMemo(() => ([
    { title: 'Total Deliveries', value: String(totalDeliveries), icon: BoxTick, color: 'text-purple-500', bgColor: 'bg-purple-50', iconColor: '#9333ea' },
    { title: 'Total Vehicles', value: String(vehicles.length), icon: Truck, color: 'text-orange-500', bgColor: 'bg-orange-50', iconColor: '#f97316' },
    { title: 'Ongoing Deliveries', value: String(ongoingDeliveries), icon:TruckFast, color: 'text-yellow-500', bgColor: 'bg-yellow-50', iconColor: '#eab308' },
    { title: 'Delivered Deliveries', value: String(deliveredDeliveries), icon: TruckTime, color: 'text-green-600', bgColor: 'bg-green-50', iconColor: '#16a34a' },
    { title: 'Upcoming Deliveries', value: String(upcomingDeliveries), icon: Box, color: 'text-purple-400', bgColor: 'bg-purple-50', iconColor: '#a855f7' },
    { title: 'Total Vendor Cost', value: formatMetricCurrency(totalVendorCost), icon: Star1, color: 'text-emerald-600', bgColor: 'bg-emerald-50', iconColor: '#059669' },
  ]), [totalDeliveries, vehicles.length, ongoingDeliveries, deliveredDeliveries, upcomingDeliveries, totalVendorCost, formatMetricCurrency]);

  // Success rate - based on total assigned trips and delivered trips
  // Total assigned trips = all trips in the date range
  // Delivered trips = trips with delivered/completed status
  const totalAssignedTrips = useMemo(() => {
    return filteredAssignments.length;
  }, [filteredAssignments]);

  const deliveredTrips = useMemo(() => {
    return filteredAssignments.filter(a => {
      const status = a.deliveryStatus?.toLowerCase() || '';
      return a.deliveryCompleted === true || 
             status === 'delivered' || 
             status === 'completedbyadmin' ||
             status === 'completedbycron' ||
             a.status === 'completed';
    }).length;
  }, [filteredAssignments]);

  const successRate = useMemo(() => {
    if (!totalAssignedTrips) return 0;
    return Math.round((deliveredTrips / totalAssignedTrips) * 100);
  }, [totalAssignedTrips, deliveredTrips]);

  // Delivery trends data - grouped by weeks (optimized)
  const deliveryTrendsData = useMemo(() => {
    // Limit processing to recent assignments for better performance
    const recentAssignments = filteredAssignments.slice(-500); // Process only last 500
    const weekData: { [key: string]: number } = {};
    const now = Date.now();
    
    recentAssignments.forEach(a => {
      if (!a.createdAt) return;
      const d = new Date(a.createdAt);
      if (isNaN(d.getTime())) return;
      
      // Calculate week number more efficiently
      const daysDiff = Math.floor((now - d.getTime()) / (24 * 60 * 60 * 1000));
      const weekNum = Math.floor(daysDiff / 7) + 1;
      if (weekNum >= 1 && weekNum <= 4) {
        const weekKey = `Week ${weekNum}`;
        weekData[weekKey] = (weekData[weekKey] || 0) + 1;
      }
    });
    
    // Generate 4 weeks of data
    const weeks = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
    return weeks.map(week => ({
      week,
      deliveries: weekData[week] || 0
    }));
  }, [filteredAssignments]);

  // Driver status distribution over weeks - based on assignments per week
  // Active = drivers with ANY assignment (any status)
  // Inactive = total drivers - assigned drivers
  const driverStatusData = useMemo(() => {
    const now = Date.now();
    
    // Get all vendor drivers (total drivers)
    const allVendorDriverNumbers = new Set<string>();
    drivers.forEach(d => {
      const phone = normalizePhone((d as any).phoneNumber || (d as any).mobileNumber);
      if (phone) allVendorDriverNumbers.add(phone);
    });
    const totalDrivers = allVendorDriverNumbers.size;
    
    // Calculate active/inactive drivers per week based on assignments
    const weekData = [];
    for (let weekIndex = 0; weekIndex < 4; weekIndex++) {
      // Calculate date range for this week (Week 1 = oldest, Week 4 = most recent)
      // Week 1 = 28-21 days ago, Week 2 = 21-14 days ago, Week 3 = 14-7 days ago, Week 4 = 7-0 days ago
      const weekStartDays = (3 - weekIndex) * 7;
      const weekEndDays = (4 - weekIndex) * 7;
      
      const weekStartTime = now - (weekStartDays * 24 * 60 * 60 * 1000);
      const weekEndTime = now - (weekEndDays * 24 * 60 * 60 * 1000);
      
      // Get assignments for this week (any status)
      const weekAssignments = scopedAssignments.filter(a => {
        if (!a.createdAt) return false;
        const assignmentTime = new Date(a.createdAt).getTime();
        return assignmentTime >= weekEndTime && assignmentTime < weekStartTime;
      });
      
      // Get unique driver numbers assigned in this week (any status)
      const weekAssignedDriverNumbers = new Set<string>();
      weekAssignments.forEach(a => {
        if (a?.driverNumber) {
          const phone = normalizePhone(a.driverNumber);
          if (phone) weekAssignedDriverNumbers.add(phone);
        }
      });
      
      // Active = drivers with any assignment in this week
      const weekActiveCount = weekAssignedDriverNumbers.size;
      
      // Inactive = total drivers - assigned drivers
      const weekInactiveCount = Math.max(0, totalDrivers - weekActiveCount);
      
      // Format date range for label
      const startDate = new Date(weekEndTime);
      const endDate = new Date(weekStartTime);
      const formatDate = (date: Date) => {
        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'short' });
        return `${day} ${month}`;
      };
      const weekLabel = `${formatDate(startDate)} - ${formatDate(endDate)}`;
      
      weekData.push({
        week: weekLabel,
        Active: weekActiveCount,
        Inactive: weekInactiveCount
      });
    }
    
    return weekData;
  }, [scopedAssignments, drivers]);

  // Calculate max value for Y-axis domain
  const maxDriverStatusValue = useMemo(() => {
    const maxActive = Math.max(...driverStatusData.map(d => d.Active || 0));
    const maxInactive = Math.max(...driverStatusData.map(d => d.Inactive || 0));
    const maxValue = Math.max(maxActive, maxInactive, 1);
    // Round up to nearest 10, minimum 10
    return Math.max(Math.ceil(maxValue / 10) * 10, 10);
  }, [driverStatusData]);

  // Violations by vehicle
  const violationsByVehicle = useMemo(() => {
    const vm = new Map<string, number>();
    filteredAssignments.forEach(a => {
      if (a.tempAbuse && a.vehicleNumber) {
        vm.set(a.vehicleNumber, (vm.get(a.vehicleNumber) || 0) + 1);
      }
    });
    const entries = Array.from(vm.entries())
      .map(([vehicle, violations]) => ({ vehicle, violations }))
      .sort((a, b) => b.violations - a.violations)
      .slice(0, 6);
    
    // Filter by search
    if (violationSearch) {
      return entries.filter(e => 
        e.vehicle.toLowerCase().includes(violationSearch.toLowerCase())
      );
    }
    return entries;
  }, [filteredAssignments, violationSearch]);

  // Vehicle Type Distribution - using real vehicle data only
  const vehicleTypeDistribution = useMemo(() => {
    if (!vehicles || vehicles.length === 0) {
      return [];
    }

    const distributionMap = new Map<string, number>();
    
    // Process only actual vehicles from the API - no mock/demo data
    vehicles.forEach(v => {
      // Check if vehicle has valid data
      if (!v || (!v.vehicleNumber && !(v as any)._id)) {
        return; // Skip invalid vehicles
      }
      
      const type = (v as any).vehicleType || (v as any).type || '';
      if (!type) return; // Skip if no type
      
      // Normalize type: trim whitespace
      const normalizedType = String(type).trim();
      if (!normalizedType || normalizedType === 'Unknown' || normalizedType === 'null') {
        return; // Skip empty or invalid types
      }
      
      // Count by exact normalized type (case-sensitive to preserve actual names)
      distributionMap.set(normalizedType, (distributionMap.get(normalizedType) || 0) + 1);
    });
    
    // Only return types that actually exist in the vehicles array
    const colors = ['#9333ea', '#3b82f6', '#eab308', '#10b981', '#f97316', '#ef4444'];
    const entries = Array.from(distributionMap.entries())
      .filter(([type, count]) => count > 0 && type) // Only include valid types with vehicles
      .map(([type, count], index) => ({
        name: type,
        value: count,
        color: colors[index % colors.length]
      }));

    
    return entries;
  }, [vehicles]);

  // Top Driver Performance - filtered and sorted
  const topDriverPerformance = useMemo(() => {
    let drivers = driverPerformance || [];
    
    // Filter by search
    if (driverSearch) {
      drivers = drivers.filter(d => 
        d.name.toLowerCase().includes(driverSearch.toLowerCase())
      );
    }
    
    // Sort by efficiency or deliveries and take top 5
    return drivers
      .sort((a, b) => (b.efficiency || 0) - (a.efficiency || 0))
      .slice(0, 5);
  }, [driverPerformance, driverSearch]);

  // Format date range for display
  const formatDateRange = () => {
    if (dateRange === 'custom' && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      return `${start.getDate()}-${start.toLocaleString('en-US', { month: 'short' })} → ${end.getDate()}-${end.toLocaleString('en-US', { month: 'short' })}`;
    }
    const days = parseInt(dateRange, 10) || 30;
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    return `${start.getDate()}-${start.toLocaleString('en-US', { month: 'short' })} → ${end.getDate()}-${end.toLocaleString('en-US', { month: 'short' })}`;
  };

  const dateRangeOptions = [
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: '365', label: 'Last year' },
    { value: 'custom', label: 'Custom range' }
  ];

  const vehicleTypeOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All Vehicles' }];
    const types = new Set(vehicles.map(v => (v as any).vehicleType || (v as any).type || 'Unknown').filter(Boolean));
    types.forEach(type => {
      options.push({ value: String(type).toLowerCase(), label: String(type) });
    });
    return options;
  }, [vehicles]);

  const driverStatusOptions = [
    { value: 'all', label: 'All Drivers' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const temperatureRangeOptions = [
    { value: 'all', label: 'All Temperatures' },
    { value: 'cold', label: 'Cold Chain (0-4°C)' },
    { value: 'frozen', label: 'Frozen (-18°C)' },
    { value: 'ambient', label: 'Ambient (15-25°C)' }
  ];

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
    if (value !== 'custom') {
      const days = parseInt(value, 10) || 30;
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - days);
      setStartDate(`${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`);
      setEndDate(`${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`);
    }
  };

  const handleDateRangeSelect = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  // Donut chart data for success rate
  const successRateData = useMemo(() => {
    const failedValue = Math.max(0, 100 - successRate);
    return [
      { name: 'Success', value: successRate, color: '#10b981' },
      { name: 'Failed', value: failedValue, color: '#e5e7eb' }
    ];
  }, [successRate]);

  const maxViolations = violationsByVehicle.length > 0 
    ? Math.max(...violationsByVehicle.map(v => v.violations))
    : 1;

  return (
    <div className="p-4 sm:p-6 bg-gray-100 min-h-full">
      {/* Page Title and Filters Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Monitor your temperature-controlled logistics operations.</p>
          </div>
          
          {/* Filters in Header */}
          {!loading && (
            <div className="flex flex-wrap items-center gap-3">
              {/* Date Range Preset Selector */}
              <select
                value={dateRange}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-jd-green bg-white"
              >
                {dateRangeOptions.filter(opt => opt.value !== 'custom').map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                <option value="custom">Custom range</option>
              </select>

              {/* Date Range Picker - Shows when custom is selected */}
              {dateRange === 'custom' ? (
                <SingleDateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onDateChange={handleDateRangeSelect}
                  placeholder="Select Date Range"
                />
              ) : (
                <div className="px-3 py-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md">
                  {formatDateRange()}
                </div>
              )}

              {/* Vehicle Type Filter */}
              <CustomDropdown
                label=""
                value={vehicleType}
                options={vehicleTypeOptions}
                onChange={setVehicleType}
                placeholder="All Vehicles"
                className="min-w-[140px]"
              />

              {/* Driver Status Filter */}
              <CustomDropdown
                label=""
                value={driverStatus}
                options={driverStatusOptions}
                onChange={setDriverStatus}
                placeholder="All Drivers"
                className="min-w-[140px]"
              />

              {/* Temperature Range Filter */}
              <CustomDropdown
                label=""
                value={temperatureRange}
                options={temperatureRangeOptions}
                onChange={setTemperatureRange}
                placeholder="All Temperatures"
                className="min-w-[160px]"
              />
            </div>
          )}
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {loading ? (
          <>
            <StatCardSkeleton iconBgColor="bg-purple-100" />
            <StatCardSkeleton iconBgColor="bg-green-100" />
            <StatCardSkeleton iconBgColor="bg-blue-100" />
            <StatCardSkeleton iconBgColor="bg-emerald-100" />
            <StatCardSkeleton iconBgColor="bg-orange-100" />
            <StatCardSkeleton iconBgColor="bg-emerald-100" />
          </>
        ) : (
          metrics.map((metric, index) => {
            const IconComponent = metric.icon;
            return (
              <div key={index} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500 mb-1">{metric.title}</p>
                    <p className="text-3xl font-bold text-gray-900">{metric.value}</p>
                  </div>
                  <div className={`${metric.bgColor} p-3 rounded-full`}>
                    <IconComponent 
                      size="24" 
                      variant="Bold" 
                      color={metric.iconColor}
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Charts Section */}
      {loading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartSkeleton />
            <ChartSkeleton />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartSkeleton />
            <ChartSkeleton />
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* First Row - Delivery Trends and Driver Status Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Delivery Trends Line Chart */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Delivery Trends</h2>
               
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={deliveryTrendsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis 
                      dataKey="week" 
                      stroke="#6b7280"
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis 
                      domain={[0, 50]}
                      stroke="#6b7280"
                      style={{ fontSize: '12px' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="deliveries" 
                      stroke="#9333ea" 
                      strokeWidth={3}
                      dot={{ fill: '#9333ea', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Driver Status Distribution Line Chart */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Driver Status Distribution</h2>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={driverStatusData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis 
                      dataKey="week" 
                      stroke="#6b7280"
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis 
                      domain={[0, maxDriverStatusValue]}
                      stroke="#6b7280"
                      style={{ fontSize: '12px' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px'
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      iconType="circle"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="Active" 
                      stroke="#9333ea" 
                      strokeWidth={3}
                      dot={{ fill: '#9333ea', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="Inactive" 
                      stroke="#f97316" 
                      strokeWidth={3}
                      dot={{ fill: '#f97316', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Second Row - Delivery Success Rate and Temperature Violations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Delivery Success Rate Donut Chart */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Delivery Success Rate</h2>
               
              </div>
              <div className="p-6">
                <div className="relative flex flex-col items-center">
                  <div className="relative" style={{ width: '300px', height: '300px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={successRateData}
                          cx="50%"
                          cy="50%"
                          innerRadius={80}
                          outerRadius={120}
                          paddingAngle={0}
                          dataKey="value"
                          startAngle={90}
                          endAngle={-270}
                        >
                          {successRateData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-gray-900">{successRate}%</div>
                        <div className="text-sm text-gray-500">Success Rate</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex gap-6">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <div>
                        <div className="text-lg font-bold text-gray-900">{deliveredTrips}</div>
                        <div className="text-xs text-gray-500">Delivered</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                      <div>
                        <div className="text-lg font-bold text-gray-900">{totalAssignedTrips - deliveredTrips}</div>
                        <div className="text-xs text-gray-500">Not Delivered</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <div>
                        <div className="text-lg font-bold text-gray-900">{totalAssignedTrips}</div>
                        <div className="text-xs text-gray-500">Total Assigned</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Temperature Violations by Vehicle */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Temperature Violations by Vehicle</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by vehicle no"
                    value={violationSearch}
                    onChange={(e) => setViolationSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-jd-green"
                  />
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {violationsByVehicle.length > 0 ? (
                    violationsByVehicle.map((item, index) => {
                      const barWidth = maxViolations > 0 ? (item.violations / maxViolations) * 100 : 0;
                      const barColor = item.violations > 20 ? 'bg-orange-500' : 'bg-green-500';
                      return (
                        <div key={index} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900">{item.vehicle}</span>
                            <span className="text-sm font-medium text-gray-700">{item.violations} Violations</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className={`${barColor} h-3 rounded-full transition-all duration-300`}
                              style={{ width: `${barWidth}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      No temperature violations found
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Third Row - Vehicle Type Distribution and Top Driver Performance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Vehicle Type Distribution Donut Chart */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Vehicle Type Distribution</h2>
               
              </div>
              <div className="p-6">
                <div className="relative flex flex-col items-center">
                  <div className="relative" style={{ width: '300px', height: '300px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={vehicleTypeDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={80}
                          outerRadius={120}
                          paddingAngle={2}
                          dataKey="value"
                          startAngle={90}
                          endAngle={-270}
                        >
                          {vehicleTypeDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-gray-900">{vehicles.length}</div>
                        <div className="text-sm text-gray-500">Total Vehicles</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 w-full space-y-2">
                    {vehicleTypeDistribution.map((entry, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: entry.color }}
                          ></div>
                          <span className="text-sm text-gray-700">{entry.name}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {entry.value} {entry.value === 1 ? 'Vehicle' : 'Vehicles'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Top Driver Performance */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
                <h2 className="text-lg font-semibold text-gray-900">Top Driver Performance</h2>
                <div className="relative w-full max-w-xs">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by driver name"
                    value={driverSearch}
                    onChange={(e) => setDriverSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-jd-green bg-white"
                  />
                </div>
              </div>
              {typeof vendorMedianRating === 'number' && !loadingTopDrivers && (
                <div className="px-6 py-3 border-b border-amber-100 bg-amber-50/70 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-gray-900">{vendorDisplayName}</span>
                    {' — median driver rating across all your drivers (same rating as each driver card).'}
                  </p>
                  <div className="shrink-0 rounded-full bg-white border border-amber-200 px-4 py-1.5 flex items-center gap-1">
                    <Star1 size="18" variant="Bold" color="#d97706" />
                    <span className="text-base font-bold text-amber-900">
                      {Number.isInteger(vendorMedianRating) ? vendorMedianRating : vendorMedianRating.toFixed(1)}
                    </span>
                   
                  </div>
                </div>
              )}
              <div className="p-6">
                <div className="space-y-4">
                  {loadingTopDrivers ? (
                    <div className="text-center text-gray-500 py-8">
                      Loading driver performance data...
                    </div>
                  ) : topDriverPerformance.length > 0 ? (
                    topDriverPerformance.map((driver, index) => (
                      <div key={index} className="flex items-start gap-6 p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                        {/* Left Section - Avatar and Info */}
                        <div className="flex items-center gap-3 min-w-[120px]">
                          <div className="w-16 h-16 bg-purple-200 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {driver.profileImageUrl ? (
                              <img 
                                src={driver.profileImageUrl} 
                                alt={driver.name}
                                className="w-full h-full object-cover rounded-full"
                              />
                            ) : (
                              <Profile2User size="32" variant="Bold" color="#9333ea" />
                            )}
                          </div>
                          <div className="text-start w-full">
                            <div className="text-base font-semibold text-gray-900 mb-2">{driver.name}</div>
                            <div className="space-y-1.5 flex flex-row items-center justify-center gap-1.5">
                              <div className="flex items-center justify-center gap-1.5">
                                <Box size="16" variant="Bold" color="#16a34a" />
                                <span className="text-sm text-gray-700">{driver.deliveries || 0} Deliveries</span>
                              </div>
                              <div className="flex items-center justify-center gap-1.5">
                                <Star1 size="16" variant="Bold" color="#eab308" />
                                <span className="text-sm text-gray-700">{driver.rating || 0} Rating</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Right Section - Efficiency Badge */}
                        <div className="flex-1 flex items-center justify-end">
                          <div className="px-4 py-2 bg-green-100 rounded-full">
                            <span className="text-sm font-semibold text-green-700">{driver.efficiency || 0}% Efficiency</span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      No driver performance data found
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
